import { useEffect } from 'react';

/**
 * Hook to lock body scroll when a modal or overlay is open
 * Prevents background scrolling and preserves scroll position
 * 
 * @param isLocked - Boolean indicating whether scroll should be locked
 * 
 * @example
 * ```tsx
 * function Modal({ isOpen }) {
 *   useBodyScrollLock(isOpen);
 *   
 *   if (!isOpen) return null;
 *   return <div>Modal content</div>;
 * }
 * ```
 */
export function useBodyScrollLock(isLocked: boolean): void {
  useEffect(() => {
    if (!isLocked) return;

    // Get original values
    const originalOverflow = window.getComputedStyle(document.body).overflow;
    const originalPaddingRight = window.getComputedStyle(document.body).paddingRight;
    const originalPosition = window.getComputedStyle(document.body).position;
    const originalTop = window.getComputedStyle(document.body).top;
    const originalWidth = window.getComputedStyle(document.body).width;

    // Save current scroll position
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;

    // Calculate scrollbar width
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    
    // Lock body scroll with enhanced styles
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    
    // Add padding to prevent layout shift when scrollbar disappears
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    // Prevent touch-based scrolling on mobile devices
    const preventTouchMove = (e: TouchEvent) => {
      // Allow scrolling within modal content
      const target = e.target as HTMLElement;
      const isScrollable = target.closest('[data-scroll-lock-ignore]') || 
                          target.closest('.overflow-y-auto') ||
                          target.closest('.overflow-auto');
      
      if (!isScrollable) {
        e.preventDefault();
      }
    };

    // Add touch event listener with passive: false to allow preventDefault
    document.addEventListener('touchmove', preventTouchMove, { passive: false });

    // Re-enable scroll on unmount
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = originalWidth;
      document.body.style.paddingRight = originalPaddingRight;
      
      // Restore scroll position
      window.scrollTo(scrollX, scrollY);
      
      // Remove touch event listener
      document.removeEventListener('touchmove', preventTouchMove);
    };
  }, [isLocked]);
}

